import { OrderEventListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency, getFulfillmentTypeName } from "@sellar-io/common";
import { ReactElement } from "react";
import { Icon } from "../../../../components";
import { ActionPanel } from "../../../../components/actionPanel/ActionPanel";
import { IconType, H5, H6 } from "@sellar-io/components";
import { getFulfillmentChangeReasonOptions } from "../SellarDistributionFulfillmentChangeFormFragment/lib/getFulfillmentChangeReasonOptions";

export function OrderEventsAdditionalInformation({
  eventData,
}: {
  eventData: OrderEventListResponseType[number]["data"];
}): ReactElement | null {
  const getEventAdditionalInfo = (): {
    notes: string | null;
    icon?: IconType;
    title?: string;
    reference?: string | null;
    amountChange?: number | null;
    fulfillmentTypeChange?: {
      previousValue: string;
      currentValue: string;
    };
  } | null => {
    switch (eventData.type) {
      case "refund": {
        return {
          icon: "ARROW_U_LEFT_TOP",
          title: "Refunded",
          amountChange: eventData.refundAmount,
          notes: eventData.notes,
        };
      }
      case "partial_refund": {
        return {
          icon: "ARROW_U_LEFT_TOP",
          title: "Partially refunded",
          amountChange: eventData.refundAmount,
          notes: eventData.notes,
        };
      }
      case "cancelled": {
        return {
          notes: eventData.notes,
        };
      }
      case "voided": {
        return {
          notes: eventData.reason,
        };
      }
      case "void-financial-resolution-acknowledged": {
        return {
          notes: eventData.note,
          reference: eventData.reference,
        };
      }
      case "updated": {
        const fulfillmentTypeChange = eventData.changes?.find(
          (change) => change.field === "fulfillmentType",
        );

        if (!fulfillmentTypeChange) return null;

        return {
          icon: "TRUCK",
          title: "Delivery method changed",
          notes: getFulfillmentTypeChangeMessage({
            change: fulfillmentTypeChange,
          }),
          fulfillmentTypeChange: {
            previousValue: getFulfillmentTypeName(
              fulfillmentTypeChange.previousValue,
            ),
            currentValue: getFulfillmentTypeName(
              fulfillmentTypeChange.currentValue,
            ),
          },
        };
      }
      default: {
        return null;
      }
    }
  };
  const eventsAdditionalInfo = getEventAdditionalInfo();

  if (!eventsAdditionalInfo) return null;

  return (
    <ActionPanel title={""} className={"p-3"}>
      <div className="flex flex-col gap-2">
        {eventsAdditionalInfo.icon && (
          <div className="flex w-full flex-row justify-between">
            <div className="flex flex-row gap-2">
              <Icon icon={eventsAdditionalInfo.icon} />
              {eventsAdditionalInfo.title && (
                <H5>{eventsAdditionalInfo.title}</H5>
              )}
            </div>
            {eventsAdditionalInfo.amountChange && (
              <H6>{formatCurrency(eventsAdditionalInfo.amountChange)}</H6>
            )}
          </div>
        )}
        {eventsAdditionalInfo.notes && (
          <H6 className={"text-wrap italic"}>"{eventsAdditionalInfo.notes}"</H6>
        )}
        {eventsAdditionalInfo.reference && (
          <H6 className="text-wrap text-zinc-500">
            Reference: {eventsAdditionalInfo.reference}
          </H6>
        )}
        {eventsAdditionalInfo.fulfillmentTypeChange && (
          <div className="flex flex-row items-center gap-2 text-sm font-semibold text-zinc-500">
            <span>
              {eventsAdditionalInfo.fulfillmentTypeChange.previousValue}
            </span>
            <Icon icon="ARROW_RIGHT" size={0.8} />
            <span>
              {eventsAdditionalInfo.fulfillmentTypeChange.currentValue}
            </span>
          </div>
        )}
      </div>
    </ActionPanel>
  );
}

type UpdatedOrderEventData = Extract<
  OrderEventListResponseType[number]["data"],
  { type: "updated" }
>;

type FulfillmentTypeChange = Extract<
  NonNullable<UpdatedOrderEventData["changes"]>[number],
  { field: "fulfillmentType" }
>;

const getFulfillmentTypeChangeMessage = ({
  change,
}: {
  change: FulfillmentTypeChange;
}): string | null => {
  const options = getFulfillmentChangeReasonOptions({
    newFulfillmentType: change.currentValue,
  });
  const selectedOptionLabel = options.find(
    (option) => option.value === change.reason,
  )?.label;

  return [selectedOptionLabel, change.notes].filter(Boolean).join(": ");
};
